import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Paper,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Collapse
} from '@mui/material';
import { 
  MenuBook as CookbookIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  LocalOffer as TagIcon
} from '@mui/icons-material';
import { supabase } from '../utils/supabase';
import RecipeSearchBar from '../components/RecipeSearchBar';
import { 
  searchRecipesByTitleAndTags, 
  getEnhancedSearchSummary,
  getSearchMatchInfo 
} from '../utils/searchUtils';
import RecipeTagDialog from '../components/RecipeTagDialog';
import TagChip from '../components/TagChip';

export default function CookbookPage({ session }) {
  const navigate = useNavigate();
  const { recipeId } = useParams();
  
  // State management
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [expandedRecipes, setExpandedRecipes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [recipeForTagging, setRecipeForTagging] = useState(null);
  const [recipeTags, setRecipeTags] = useState({});
  
  // Form state for editing
  const [editForm, setEditForm] = useState({
    title: '',
    ingredients_text: '',
    instructions_text: '',
    notes: ''
  });

  // Computed filtered recipes based on search query
  const filteredRecipes = useMemo(() => {
    return searchRecipesByTitleAndTags(recipes, searchQuery, recipeTags);
  }, [recipes, searchQuery, recipeTags]);

  // Load recipes on component mount
  useEffect(() => {
    loadRecipes();
  }, []);

  // Load specific recipe if recipeId is provided
  useEffect(() => {
    if (recipeId && recipes.length > 0) {
      const recipe = recipes.find(r => r.id === recipeId);
      if (recipe) {
        setSelectedRecipe(recipe);
        setExpandedRecipes(new Set([recipeId]));
      }
    }
  }, [recipeId, recipes]);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      setError(null);
            
      // Check authentication
      if (!session?.user) {
        setError('Please log in to view your cookbook');
        return;
      }
      
      // Create a timeout promise to race against the REST API call
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('REST API timeout'));
        }, 10000);
      });
      
      // Use direct REST API call to fetch recipes instead of missing Edge Function
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipes?user_id=eq.${session.user.id}&order=created_at.desc`;
      const fetchPromise = fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const recipes = await response.json();
      
      if (Array.isArray(recipes)) {
        setRecipes(recipes);
        // Load tags for all recipes
        if (recipes.length > 0) {
          await loadRecipeTags(recipes.map(r => r.id));
        }
      } else {
        setRecipes([]);
      }

    } catch (err) {
      console.error('📚 Error loading recipes:', err);
      if (err.message?.includes('timeout')) {
        setError('Request timed out. Please try refreshing the page or logging out and back in.');
      } else {
        setError('Failed to load recipes');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecipeTags = async (recipeIds) => {
    try {
      if (!recipeIds || recipeIds.length === 0) return;

      // Use direct REST API call to avoid session hanging issues
      // For PostgREST, use 'in' operator for multiple values
      const recipeIdsString = recipeIds.join(',');
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipe_tag_assignments?recipe_id=in.(${recipeIdsString})&select=recipe_id,recipe_tags(id,name,color)`;
      
      // Create a timeout promise to race against the query
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error('Tags query timeout'));
        }, 5000);
      });
      
      const fetchPromise = fetch(url, {
        method: 'GET',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const response = await Promise.race([fetchPromise, timeoutPromise]);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('🏷️ Tags API error:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();

      // Group tags by recipe ID
      const tagsByRecipe = {};
      data?.forEach(assignment => {
        const recipeId = assignment.recipe_id;
        if (!tagsByRecipe[recipeId]) {
          tagsByRecipe[recipeId] = [];
        }
        tagsByRecipe[recipeId].push(assignment.recipe_tags);
      });

      setRecipeTags(tagsByRecipe);
    } catch (err) {
      console.error('Error loading recipe tags:', err);
    }
  };

  const handleTagsUpdated = () => {
    // Reload tags for all recipes
    const recipeIds = recipes.map(r => r.id);
    loadRecipeTags(recipeIds);
  };

  const handleManageTags = (recipe) => {
    setRecipeForTagging(recipe);
    setTagDialogOpen(true);
  };

  const handleEditRecipe = (recipe) => {
    setEditForm({
      title: recipe.title,
      ingredients_text: recipe.ingredients_text,
      instructions_text: recipe.instructions_text,
      notes: recipe.notes || ''
    });
    setSelectedRecipe(recipe);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Check session
      if (!session) {
        setError('Please log in to edit recipes');
        return;
      }
      
      // Use direct REST API call to update recipe instead of missing Edge Function
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipes?id=eq.${selectedRecipe.id}`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation'
        },
        body: JSON.stringify({
          title: editForm.title,
          ingredients_text: editForm.ingredients_text,
          instructions_text: editForm.instructions_text,
          notes: editForm.notes
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const updatedRecipes = await response.json();
      const updatedRecipe = updatedRecipes[0]; // PATCH returns an array
      
      if (!updatedRecipe) {
        throw new Error('Failed to update recipe');
      }
      
      // Update the recipes list
      setRecipes(prev => prev.map(recipe => 
        recipe.id === selectedRecipe.id ? updatedRecipe : recipe
      ));
      
      // Update selected recipe if it's the one being edited
      if (selectedRecipe && selectedRecipe.id === updatedRecipe.id) {
        setSelectedRecipe(updatedRecipe);
      }
      
      setEditDialogOpen(false);
      
    } catch (err) {
      setError('Failed to update recipe');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (recipe) => {
    setRecipeToDelete(recipe);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // Check session
      if (!session) {
        setError('Please log in to delete recipes');
        return;
      }
      
      // Use direct REST API call to delete recipe instead of missing Edge Function
      const url = `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/recipes?id=eq.${recipeToDelete.id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Remove from recipes list
      setRecipes(prev => prev.filter(recipe => recipe.id !== recipeToDelete.id));
      
      // Clear selected recipe if it was the deleted one
      if (selectedRecipe && selectedRecipe.id === recipeToDelete.id) {
        setSelectedRecipe(null);
      }
      
      setDeleteDialogOpen(false);
      setRecipeToDelete(null);
      
    } catch (err) {
      setError('Failed to delete recipe');
    } finally {
      setSaving(false);
    }
  };

  const toggleRecipeExpansion = (recipeId) => {
    const newExpanded = new Set(expandedRecipes);
    if (newExpanded.has(recipeId)) {
      newExpanded.delete(recipeId);
    } else {
      newExpanded.add(recipeId);
    }
    setExpandedRecipes(newExpanded);
  };

  const formatTextToList = (text) => {
    if (!text) return [];
    return text.split('\n').filter(line => line.trim());
  };

  // Function to extract macronutrients from notes
  const extractMacronutrients = (notes) => {
    if (!notes) return null;
    
    const nutritionMatch = notes.match(/Nutrition \(per serving\):([\s\S]*?)(?=\n\n|$)/);
    if (!nutritionMatch) return null;
    
    const nutritionText = nutritionMatch[1];
    const macros = {};
    
    const caloriesMatch = nutritionText.match(/Calories:\s*(\d+(?:\.\d+)?)/i);
    const proteinMatch = nutritionText.match(/Protein:\s*(\d+(?:\.\d+)?)/i);
    const carbsMatch = nutritionText.match(/Carbohydrates:\s*(\d+(?:\.\d+)?)/i);
    const fatMatch = nutritionText.match(/Fat:\s*(\d+(?:\.\d+)?)/i);
    const fiberMatch = nutritionText.match(/Fiber:\s*(\d+(?:\.\d+)?)/i);
    
    if (caloriesMatch) macros.calories = caloriesMatch[1];
    if (proteinMatch) macros.protein = proteinMatch[1];
    if (carbsMatch) macros.carbohydrates = carbsMatch[1];
    if (fatMatch) macros.fat = fatMatch[1];
    if (fiberMatch) macros.fiber = fiberMatch[1];
    
    return Object.keys(macros).length > 0 ? macros : null;
  };

  // Function to get notes without nutrition info
  const getNotesWithoutNutrition = (notes) => {
    if (!notes) return '';
    return notes.replace(/\n\nNutrition \(per serving\):([\s\S]*?)$/, '').trim();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
          <CookbookIcon sx={{ mr: 2, color: 'primary.main' }} />
          My Cookbook
        </Typography>
      </Box>

      {/* Search Bar */}
      <RecipeSearchBar 
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        placeholder="Search your recipes by title or tags..."
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Header Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          My Recipes ({recipes.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/generate-recipe')}
        >
          Generate Recipe
        </Button>
      </Box>

      {/* Search Results Summary */}
      {searchQuery && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            {getEnhancedSearchSummary(filteredRecipes, searchQuery, recipeTags)}
          </Typography>
        </Box>
      )}

      {recipes.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CookbookIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No recipes saved yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Generate your first recipe and save it to your cookbook!
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/generate-recipe')}
          >
            Generate Recipe
          </Button>
        </Paper>
      ) : filteredRecipes.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CookbookIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No recipes found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Try a different search term or clear your search to see all recipes.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => setSearchQuery('')}
          >
            Clear Search
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredRecipes.map((recipe) => {
            const isExpanded = expandedRecipes.has(recipe.id);
            return (
              <Grid item xs={12} sm={6} lg={4} key={recipe.id}>
                <Card 
                  sx={{ 
                    height: 'fit-content',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: 4 
                    }
                  }}
                >
                  <CardContent>
                    {/* Recipe Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        flex: 1,
                        mr: 1
                      }}>
                        {recipe.title}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleManageTags(recipe)}
                          title="Manage Tags"
                        >
                          <TagIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleEditRecipe(recipe)}
                          title="Edit Recipe"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => handleDeleteClick(recipe)}
                          title="Delete Recipe"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    {/* Recipe Meta */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(recipe.created_at)}
                      </Typography>
                    </Box>

                    {/* Recipe Tags */}
                    {recipeTags[recipe.id] && recipeTags[recipe.id].length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {recipeTags[recipe.id].slice(0, 3).map(tag => (
                            <TagChip key={tag.id} tag={tag} size="small" />
                          ))}
                          {recipeTags[recipe.id].length > 3 && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              +{recipeTags[recipe.id].length - 3} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                    
                    {/* Recipe Preview */}
                    <Typography variant="body2" sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      mb: 2,
                      color: 'text.secondary'
                    }}>
                      {formatTextToList(recipe.ingredients_text).slice(0, 3).join(', ')}
                      {formatTextToList(recipe.ingredients_text).length > 3 && '...'}
                    </Typography>
                    
                    {/* Expand Button */}
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      onClick={() => toggleRecipeExpansion(recipe.id)}
                      sx={{ mb: 2 }}
                    >
                      {isExpanded ? 'Hide Recipe' : 'View Full Recipe'}
                    </Button>
                    
                    {/* Expanded Recipe Details */}
                    <Collapse in={isExpanded}>
                      <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        {/* Ingredients */}
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Ingredients:
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                          {formatTextToList(recipe.ingredients_text).map((ingredient, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                              • {ingredient}
                            </Typography>
                          ))}
                        </Box>
                        
                        {/* Instructions */}
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Instructions:
                        </Typography>
                        <Box sx={{ mb: 3 }}>
                          {formatTextToList(recipe.instructions_text).map((instruction, idx) => (
                            <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                              {idx + 1}. {instruction}
                            </Typography>
                          ))}
                        </Box>
                        
                        {/* Macronutrients */}
                        {(() => {
                          const macros = extractMacronutrients(recipe.notes);
                          return macros && (
                            <>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Nutrition (per serving):
                              </Typography>
                              <Box sx={{ mb: 3 }}>
                                {macros.calories && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Calories: {macros.calories} kcal
                                  </Typography>
                                )}
                                {macros.protein && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Protein: {macros.protein} g
                                  </Typography>
                                )}
                                {macros.carbohydrates && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Carbohydrates: {macros.carbohydrates} g
                                  </Typography>
                                )}
                                {macros.fat && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Fat: {macros.fat} g
                                  </Typography>
                                )}
                                {macros.fiber && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Fiber: {macros.fiber} g
                                  </Typography>
                                )}
                              </Box>
                            </>
                          );
                        })()}
                        
                        {/* Notes */}
                        {(() => {
                          const notesWithoutNutrition = getNotesWithoutNutrition(recipe.notes);
                          return notesWithoutNutrition && (
                            <>
                              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Notes:
                              </Typography>
                              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {notesWithoutNutrition}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Edit Recipe Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Recipe</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <TextField
              label="Recipe Title"
              fullWidth
              value={editForm.title}
              onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
            />
            <TextField
              label="Ingredients"
              fullWidth
              multiline
              rows={6}
              value={editForm.ingredients_text}
              onChange={(e) => setEditForm(prev => ({ ...prev, ingredients_text: e.target.value }))}
              placeholder="Enter each ingredient on a new line"
            />
            <TextField
              label="Instructions"
              fullWidth
              multiline
              rows={8}
              value={editForm.instructions_text}
              onChange={(e) => setEditForm(prev => ({ ...prev, instructions_text: e.target.value }))}
              placeholder="Enter each instruction step on a new line"
            />
            <TextField
              label="Notes (Optional)"
              fullWidth
              multiline
              rows={3}
              value={editForm.notes}
              onChange={(e) => setEditForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Any additional notes or tips"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Recipe</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{recipeToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recipe Tag Management Dialog */}
      <RecipeTagDialog
        open={tagDialogOpen}
        onClose={() => {
          setTagDialogOpen(false);
          setRecipeForTagging(null);
        }}
        recipe={recipeForTagging}
        onTagsUpdated={handleTagsUpdated}
        session={session}
      />
    </Container>
  );
}
